import React from 'react';
import { NextRouter } from 'next/router';
import { i18nStore } from '../../../contexts/i18n';
import { DsoProduct } from '../../../types/dso/DsoProduct.types';
import { I18nLabels } from './DSOSearchField.types';

/**
 * Sets input value as state value with length limit
 *
 * **Flow:**
 * 1. Limit input length to 200 characters for performance
 * 2. Always update state with the input value (preserves spaces)
 * 3. Parent component controls dropdown visibility via minCharacters validation
 *
 * @param e - Input change event
 * @param setSearchText - State setter function
 * @remarks Length limit prevents excessive processing on very long strings.
 * Trimming is handled downstream in filterProducts() and handleKeyPress() where appropriate.
 */
export const handleChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  setSearchText: React.Dispatch<React.SetStateAction<string>>
): void => {
  const inputValue = e.target.value.slice(0, 200);
  setSearchText(inputValue);
};

/**
 * Validates search query
 * @param query - Search query to validate
 * @param minLength - Minimum acceptable length (default: 1)
 * @param maxLength - Maximum acceptable length (default: 200)
 * @returns True if valid, false otherwise
 */
export const validateSearchQuery = (
  query: string,
  minLength = 1,
  maxLength = 200
): boolean => {
  if (!query) return false;
  const trimmed = query.trim();
  return trimmed.length >= minLength && trimmed.length <= maxLength;
};

/**
 * Handles Enter key press in search input
 *
 * @param event - Keyboard event
 * @param searchText - Current search text
 * @param router - Next.js router instance (reserved for future navigation)
 * @param onSearchSubmit - Optional callback for search submission
 *
 * @remarks
 * Navigation to search results page is intentionally deferred pending UX design approval.
 * Current behavior: Invokes optional callback only. Enter key on highlighted suggestion
 * in the dropdown will navigate to product detail page (handled in DSOSuggestionList).
 *
 * @future When search results page is implemented, uncomment navigation:
 * `router.push(\`/dso/search?keyword=\${encodeURIComponent(trimmedValue)}\`);`
 */
export const handleKeyPress = (
  event: React.KeyboardEvent,
  searchText: string,
  router: NextRouter,
  onSearchSubmit?: (searchText: string) => void
): void => {
  if (event.key === 'Enter') {
    const trimmedValue = searchText.trim();
    if (trimmedValue.length < 3) return;

    // Call optional callback if provided
    if (onSearchSubmit) {
      onSearchSubmit(trimmedValue);
    }

    router.push({
      pathname: '/dso/searchresults',
      query: { keyword: trimmedValue },
    });

    // Navigation intentionally deferred - pending search results page implementation
    Future: router.push(
      `/dso/searchresults?keyword=${encodeURIComponent(trimmedValue)}`
    );
  }
};

/**
 * Shows the search modal when focusing on the input
 * @param setShowSearchModal - State setter function
 */
export const onFocusHandler = (
  setShowSearchModal: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  setShowSearchModal(true);
};

/**
 * Hides the search results modal
 * @param setShowSearchModal - State setter function
 */
export const hideSearchResultsModal = (
  setShowSearchModal: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  setShowSearchModal(false);
};

/**
 * Navigates user to product page when clicking on suggestion
 * @param slug - Product slug
 * @param hideSearchResultsModal - Function to hide the modal
 * @param router - Next.js router instance
 */
export const handleOnClick = (
  slug: string | null,
  hideSearchResultsModal: () => void,
  router: NextRouter
): void => {
  if (slug) {
    router.push(`/dso/product/${slug}`);
    hideSearchResultsModal();
  }
};

/**
 * Filters products based on search text
 * Searches in both name and sku fields (case-insensitive)
 * Returns empty array if search text is less than 3 characters
 *
 * @param searchText - The search query
 * @param products - Array of products to filter
 * @param maxResults - Maximum number of results to return (default: 8)
 * @param showAll - When true, ignore maxResults and return all matching results (default: true)
 * @returns Filtered array of products
 */
export const filterProducts = (
  searchText: string,
  products: DsoProduct[] | undefined,
  maxResults = 8,
  showAll = true
): DsoProduct[] => {
  const query = searchText.trim().toLowerCase();

  // Return empty array if query is less than 3 characters
  if (!query || query.length < 3) return [];

  // Return empty array if no products
  if (!products || products.length === 0) return [];

  const filtered = products.filter(
    product =>
      product?.name?.toLowerCase().includes(query) ||
      product?.sku?.toLowerCase().includes(query)
  );

  return showAll ? filtered : filtered.slice(0, maxResults);
};

/**
 * Returns i18n labels for the search field
 * @param i18n - i18n store object
 * @returns Object containing translated labels
 */
export const label = (i18n: i18nStore): I18nLabels => {
  return {
    popular: i18n['Popular Searches'] ?? 'Popular Searches',
    recent: i18n['Recent Searches'] ?? 'Recent Searches',
    suggestion: i18n['Search Suggestions'] ?? 'Search Suggestions',
    noresults: i18n['No Results Found'] ?? 'No Results Found',
    errorOccured:
      i18n['Error while fetching search suggestions'] ??
      'Error while fetching search suggestions',
    placeholder: i18n['Search for products'] ?? 'Search for products',
  };
};
